// Product photos are checked and downsized in the browser before upload (DEC-041): the prototype's type and
// 8 MB rules, then at most 1600 px on the long side, re-encoded as WebP (JPEG where WebP encoding is missing).
// Re-encoding also drops EXIF metadata such as GPS coordinates.

export const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
const PHOTO_MAX_BYTES = 8 * 1024 * 1024;
const MAX_SIDE = 1600;

/** Prototype messages (`onImg`). */
export function photoFileError(file: File): string | null {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return "Folosește JPG, PNG sau WebP.";
  if (file.size > PHOTO_MAX_BYTES) return "Fotografia depășește 8 MB.";
  return null;
}

export function fitWithin(width: number, height: number, max = MAX_SIDE): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

/** `maxSide`: label photos keep more detail (small print) than product photos. */
export async function preparePhoto(file: File, maxSide = MAX_SIDE): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("Fotografia nu a putut fi citită. Încearcă alt fișier.");
  }
  const size = fitWithin(bitmap.width, bitmap.height, maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Fotografia nu a putut fi pregătită. Încearcă din nou.");
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  const webp = await toBlob(canvas, "image/webp", 0.82);
  if (webp?.type === "image/webp") return webp;
  // No WebP encoder: JPEG on a white background (JPEG has no transparency).
  ctx.globalCompositeOperation = "destination-over";
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, size.width, size.height);
  const jpeg = await toBlob(canvas, "image/jpeg", 0.85);
  if (!jpeg) throw new Error("Fotografia nu a putut fi pregătită. Încearcă din nou.");
  return jpeg;
}
